import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FolderIcon } from '../../drive/folder_icon.component';

import { RootState, CommonGlobalService, CommonDocumentService, DownloadButton, DocumentViewer, FileIcon, FileImgIcon, FilePdfIcon, FileTxtIcon, FileType } from '@umanforge/common';
import { ClBaseSearch } from 'interfaces/api_deep_search.interfaces';
import { BaseCardComponent } from '../baseCard.component';

import { CloudService } from 'services/cloud.service';
import { useNavigate } from 'react-router-dom';
import { NavigationService } from 'services/navigation.service';


interface PlActionCardProps {
  doc: ClBaseSearch;
}

const ClFileCardComponent: React.FC<PlActionCardProps> = ( { doc } ) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );

  const [ filename, setFilename ] = useState<string>( '' );
  const [ fileIcon, setFileIcon ] = useState<JSX.Element>( <></> );
  const [ canView, setCanView ] = useState<boolean>( false );

  const [ viewerIsLoading, setViewerIsLoading ] = useState<boolean>( false );
  const [ viewerUrl, setViewerUrl ] = useState<string | null>( null );
  const [ viewerName, setViewerName ] = useState<string | null>( null );
  const [ viewerMimeType, setViewerMimeType ] = useState<string | null>( null );

  useEffect( () => {
    updateFileIcon();
    updateFilename();
    updateCanView();
  }, [ doc ] );


  const updateFilename = () => {
    setFilename( doc.name + ( doc.extension ?? '' ) );
  };
  const updateCanView = () => {
    setCanView( CommonDocumentService.isViewableExtension( doc.extension ? doc.extension.replace( /^\./, '' ).toLowerCase() : undefined ) );
  };
  const updateFileIcon = () => {
    if ( doc.elementCount != null ) {
      const subtitle = doc.elementCount === 0
        ? t( 'translation:0_items' )
        : doc.elementCount === 1
          ? t( 'translation:1_item' )
          : `${ doc.elementCount } ${ t( 'translation:items' ) }`;
      setFileIcon(
        <div className='flex flex-col pt-3 items-center'>
          <FolderIcon
            isEmpty={ doc.elementCount === 0 }
            className={ `w-12 h-12 ${ doc.elementCount === 0 ? 'text-orange-400' : 'text-red-500' }` }
          />
          <span className="text-xs text-gray-500">{ subtitle }</span>
        </div>
      );
    } else {
      switch ( CommonGlobalService.getFileType( doc.path ) ) {
      case FileType.IMAGE:
        setFileIcon(
          <img
            src={ doc.path }
            className='w-full h-full object-cover'
            onError={ () => {
              setCanView( false );
              setFileIcon( <FileImgIcon className='w-full h-full'/> );
            } }
          />
        );
        break;
      case FileType.PDF:
        setFileIcon( <FilePdfIcon className='w-full h-full'/> );
        break;
      case FileType.TEXT:
        setFileIcon( <FileTxtIcon className='w-full h-full'/> );
        break;
      default:
        setFileIcon( <FileIcon className='w-full h-full'/> );
        break;
      }
    }
  };

  async function openViewerForItem( id: string, name: string ) {
    setViewerIsLoading( true );
    try {
      await CloudService.downloadCloudDirFile( id, token, dispatch )
        .then( blob => {
          if ( !blob ) { return; }
          const url = window.URL.createObjectURL( blob );
          if ( doc.path ) {
            window.URL.revokeObjectURL( doc.path );
          }

          setViewerUrl( url );
          setViewerName( name );
          setViewerMimeType( blob.type ?? null );
        } );
    } catch ( err ) {
      console.warn( 'drive.view: failed to open viewer for', id, err );
    } finally {
      setViewerIsLoading( false );
    }
  }

  return (
    <>
      <BaseCardComponent
        id={ String( doc.index ) }
        title={ doc.siteName }
        breadcrumb={ `${ doc.clientName } / ${ doc.siteName }` }
        application={ doc.application }
        firstChildren={
          <div className='flex w-full h-full gap-3 items-center'>
            <DownloadButton
              fileInfo={{ name: filename, url: doc.path }}
              downloadFile={ () => { return CloudService.downloadCloudDirFile( doc.id, token, dispatch ); } }
            />
            <span className='italic text-sm text-gray-900 underline' >
              { filename }
            </span>
          </div>
        }
        secondeChildren={
          <div className='flex h-full w-fit p-2 items-center justify-center'>
            <div
              className={ `h-20 w-20 rounded-md overflow-hidden ${ canView ? 'cursor-pointer' : '' }`}
              onClick={ () => {
                if ( doc.elementCount != null ) {
                  const option = { clientId: doc.clientId, siteId: doc.siteId, cloudDirFileId: doc.id };
                  NavigationService.toCloud( navigate, false, option );
                } else if ( canView ) {
                  openViewerForItem( doc.id, filename );
                }
              } }
            >
              { fileIcon }
            </div>
          </div>
        }
      />
      <DocumentViewer
        url={ viewerUrl }
        originalName={ viewerName }
        mimeType={ viewerMimeType }
        isLoading={ viewerIsLoading }
        onClose={ () => {
          setViewerUrl( null );
          setViewerName( null );
          setViewerMimeType( null );
        } }
      />
    </>
  );
};

export { ClFileCardComponent };